import { parseISO } from 'date-fns';
import { ChangeRequest } from '../../change-request';
import { type Image, createImage } from '../../image';
import type { EventPrivacy } from '../../privacy';
import type { ProgramEvent, ProgramEventStatus, ProgramEventType, ProgramPublicEvent } from '../../program-event';
import { createProgramEvent, createProgramPublicEvent } from '../../program-event';
import type {
  ContentObjectEventDataObject,
  EventContentObjectDataObject,
  EventContentObjectListDataObject,
  NodeEventContentObjectDataObject,
  NodeEventContentObjectListDataObject,
  ProgramEventContentObjectDataObject,
  ProgramEventContentObjectListDataObject,
  PublicEventContentObjectDataObject,
  PublicNodeEventContentObjectDataObject,
  PublicProgramEventContentObjectDataObject,
} from '../../service';
import { type BaseContentObject, createBaseContentObject } from '../base-content-object';
import {
  type ProgramContentObject,
  type PublicProgramContentObject,
  createProgramContentObject,
  createPublicProgramContentObject,
} from '../contentable-type-content-objects';

export type EventContentObjectDetail = NodeEventContentObjectDetail | ProgramEventContentObjectDetail;

// TODO: on stage some events CO has contentable_type, that situation shouldnt be, event should have no contentable type
// this seems like broken migration, investigate what is the problem
export const createEventContentObject = (data: EventContentObjectDataObject): EventContentObjectDetail => {
  if (!('contentable_type' in data)) {
    return createNodeEventContentObjectDetail(data);
  }

  return createProgramEventContentObjectDetail(data);
};

export type NodeEventContentObjectDetail = {
  contentableType?: never;
  event: ProgramEvent;
  type: 'event';
} & BaseContentObject;

export const createNodeEventContentObjectDetail = (
  data: NodeEventContentObjectDataObject,
): NodeEventContentObjectDetail => ({
  ...createBaseContentObject(data),
  event: createProgramEvent(data.event),
  type: 'event',
});

export type ProgramEventContentObjectDetail = {
  event: ProgramEvent;
  type: 'event';
} & ProgramContentObject;

export const createProgramEventContentObjectDetail = (
  data: ProgramEventContentObjectDataObject,
): ProgramEventContentObjectDetail => ({
  ...createProgramContentObject(data),
  event: createProgramEvent(data.event),
  type: 'event',
});

export type PublicEventContentObject = PublicNodeEventContentObject | PublicProgramEventContentObject;

export const createPublicEventContentObject = (data: PublicEventContentObjectDataObject): PublicEventContentObject => {
  if (!('contentable_type' in data)) {
    return createPublicNodeEventContentObject(data);
  }

  return createPublicProgramEventContentObject(data);
};

export type PublicNodeEventContentObject = {
  contentableType?: never;
  event: ProgramPublicEvent;
  type: 'event';
};

export const createPublicNodeEventContentObject = (
  data: PublicNodeEventContentObjectDataObject,
): PublicNodeEventContentObject => ({
  event: createProgramPublicEvent(data.event),
  type: 'event',
});

export type PublicProgramEventContentObject = {
  event: ProgramPublicEvent;
  type: 'event';
} & PublicProgramContentObject;

export const createPublicProgramEventContentObject = (
  data: PublicProgramEventContentObjectDataObject,
): PublicProgramEventContentObject => ({
  ...createPublicProgramContentObject(data),
  event: createProgramPublicEvent(data.event),
  type: 'event',
});

export type ContentObjectEvent = {
  changeRequests?: ChangeRequest[];
  coverPhoto?: Image;
  endTime: Date;
  eventType: ProgramEventType;
  goingInviteesCount: number;
  htmlContent: null | string;
  id: string;
  name: string;
  nodeId?: string;
  startTime: Date;
  status: ProgramEventStatus;
  viewPermission: EventPrivacy;
};

export const createContentObjectEvent = (data: ContentObjectEventDataObject): ContentObjectEvent => ({
  changeRequests: data.change_requests?.map((changeRequest) => new ChangeRequest(changeRequest)),
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  endTime: parseISO(data.end_time),
  eventType: data.event_type,
  goingInviteesCount: data.going_invitees_count,
  htmlContent: data.html_content,
  id: data.id,
  name: data.name,
  nodeId: data.node_id,
  startTime: parseISO(data.start_time),
  status: data.status,
  viewPermission: data.view_permission,
});

export type EventContentObjectList = NodeEventContentObjectList | ProgramEventContentObjectList;

export const createEventContentObjectList = (data: EventContentObjectListDataObject): EventContentObjectList => {
  if (!('contentable_type' in data) || data.contentable_type === 'Community') {
    return createNodeEventContentObjectList(data);
  }

  return createProgramEventContentObjectList(data);
};

export type NodeEventContentObjectList = {
  contentableType?: never;
  event: ContentObjectEvent;
  type: 'event';
} & BaseContentObject;

export const createNodeEventContentObjectList = (
  data: NodeEventContentObjectListDataObject,
): NodeEventContentObjectList => ({
  ...createBaseContentObject(data),
  event: createContentObjectEvent(data.event),
  type: 'event',
});

export const isNodeEventContentObject = (contentObject: {
  contentableType?: string;
  event?: unknown;
}): contentObject is NodeEventContentObjectDetail | NodeEventContentObjectList | PublicNodeEventContentObject =>
  !contentObject.contentableType && Boolean(contentObject.event);

export type ProgramEventContentObjectList = {
  event: ContentObjectEvent;
  type: 'event';
} & ProgramContentObject;

export const createProgramEventContentObjectList = (
  data: ProgramEventContentObjectListDataObject,
): ProgramEventContentObjectList => ({
  ...createProgramContentObject(data),
  event: createContentObjectEvent(data.event),
  type: 'event',
});
